import React, { useEffect, useRef } from 'react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ASSETS_URL } from '../../../Settings';
import {
    DEFAULT_ACTION_HEX_POSITION,
} from '../../Game/GamePlayers/GamePlayerController';
import { getTextureUrl } from '../../TwoD/Common/TextureTools';

import './ActionHex.css';
import '../../TwoD/Components/Controls/Actions/action-scroller.css';

// 12-slide layout: two per action, in ascending action_id order.
//   index 0,1   → Research (texture 71)
//   index 2,3   → Lodging  (texture 72)
//   index 4,5   → Staff    (texture 73)
//   index 6,7   → Renovation (texture 74)
//   index 8,9   → Advertisement (texture 75)
//   index 10,11 → Ghost (texture 76)
// With slidesPerView=6 and centeredSlides=false the leftmost visible
// slide is `swiper.realIndex`. The visible-center pair at realIndex K
// is (slideAction(K+2), slideAction(K+3)). This is the math the
// engine treats as the player's intended choice. realIndex → position
// is fixed by working back from the visible center: K=10 puts
// Research at center → pos 0; K=0 puts Lodging at center → pos 10;
// K=8 puts Ghost focus → pos 2. Identical to the source ActionChooser.
const slideAction = (slideIndex) =>
    Math.floor(((slideIndex % 12) + 12) % 12 / 2) + 1;
const realIndexToPosition = (K) => (10 - K + 12) % 12;
const positionToRealIndex = (P) => (10 - P + 12) % 12;

function ActionImage({ texture }) {
    return <img src={getTextureUrl(texture)} alt={'action'}
                data-tname={texture}/>;
}

// ActionHex — functional-component port of ActionChooser. Used by
// ActionWindow on the Desktop preset. ActionChooser stays in place
// for mobile / other presets that still mount through ActionRouter.
//
// State is all in refs: Swiper is hostile to React render cycles
// (the SwiperReact wrapper does its own DOM mutation and a
// shouldComponentUpdate=false equivalent), so re-rendering on every
// per-seat tick the way ActionPicker does would force Swiper to
// re-init and lose its position. We track:
//
// - swiperRef: the Swiper instance returned by onSwiper.
// - firstLoadRef: gate for the initial slideToLoop seed. Flipped
//   inside handleSwiper, not in onSlideChange — see the comment in
//   handleSwiper for the Swiper-loop race that drove the move.
// - currentPositionRef / currentSwiperIndexRef: the position +
//   realIndex this hex believes it's at, used to dedupe
//   slideToLoop calls and to suppress echoes back to the engine.
// - currentActionsRef: derived from currentSwiperIndexRef on every
//   drag commit; kept for parity with ActionChooser even though
//   nothing currently reads it (it was visible in the engine
//   payload during a now-removed click-emit path).
// - wasLockedRef: tracks action_hex_locked across per-seat events
//   so we can detect the lock→unlock transition that marks a new
//   turn beginning on the local seat. Seeded from the initial
//   prop so the first per-seat event doesn't read undefined and
//   misfire.
// - userInputPendingRef: set by onTouchStart / onTouchMove, the
//   guard that drops layout-reflow onSlideChange fires.
export function ActionHex(props) {
    const { app, gamePlayerData } = props;
    const seat = gamePlayerData.seat;

    const swiperRef = useRef(null);
    const firstLoadRef = useRef(true);
    const currentPositionRef = useRef(null);
    const currentSwiperIndexRef = useRef(null);
    const currentActionsRef = useRef([null, null]);
    const wasLockedRef = useRef(!!(
        gamePlayerData && gamePlayerData.action_hex_locked));
    const userInputPendingRef = useRef(false);
    // gamePlayerData is read by the per-seat event listener via a
    // ref so it stays current without re-installing the listener on
    // every render. The seated controller and seat number are
    // stable for the lifetime of the mount.
    const propsRef = useRef(props);
    propsRef.current = props;

    const isLocalSeat = () => {
        const seated = app && app.game && app.game.seatedController;
        return !!(seated && seated.seat === seat);
    };

    const isHexLocked = () => {
        const gp = propsRef.current.gamePlayerData;
        return !!(gp && gp.action_hex_locked);
    };

    useEffect(() => {
        const gamePlayerEventName = 'game-player-' + seat + '-event';

        const gamePlayerUpdateListener = (e) => {
            const gp = e.detail;
            if (!gp) return;

            if (isLocalSeat()) {
                // Lock→unlock transition = new turn beginning on the
                // local seat. Swiper's loop mode silently recomputes
                // realIndex on container reflow; the userInputPending
                // guard preserves controller state but does not pull
                // the visual back. Snap to the authoritative position
                // exactly once per turn boundary. Same discipline as
                // the opponent-seat path below: slideToLoop with
                // runCallbacks=false so the snap-settle's
                // onSlideChange does not fire the emit path.
                const wasLocked = wasLockedRef.current === true;
                const isLocked = gp.action_hex_locked === true;
                wasLockedRef.current = isLocked;
                if (wasLocked && !isLocked
                        && swiperRef.current
                        && !firstLoadRef.current) {
                    const pos = gp.action_hex_position;
                    if (pos !== null && pos !== undefined) {
                        const target = positionToRealIndex(pos);
                        if (currentSwiperIndexRef.current !== target) {
                            currentPositionRef.current = pos;
                            currentSwiperIndexRef.current = target;
                            currentActionsRef.current = [
                                slideAction(target + 2),
                                slideAction(target + 3),
                            ];
                            swiperRef.current.slideToLoop(
                                target, 0, false);
                        }
                    }
                }
                return;
            }

            // Opponent seat: backend-supplied position is
            // authoritative; mirror it. runCallbacks=false stops the
            // snap-settle from echoing back through onSlideChange.
            const incomingPosition = gp.action_hex_position;
            if (incomingPosition === null
                    || incomingPosition === undefined) return;
            if (currentPositionRef.current === incomingPosition) return;
            if (!swiperRef.current) return;
            if (firstLoadRef.current) return;
            const newRealIndex = positionToRealIndex(incomingPosition);
            currentPositionRef.current = incomingPosition;
            currentSwiperIndexRef.current = newRealIndex;
            currentActionsRef.current = [
                slideAction(newRealIndex + 2),
                slideAction(newRealIndex + 3),
            ];
            swiperRef.current.slideToLoop(newRealIndex, 0, false);
        };

        const actionHexListener = (e) => {
            if (seat !== e.detail.seat) return;
            const position = e.detail.actionHexPosition;
            if (currentPositionRef.current === position) return;
            if (!swiperRef.current) return;
            swiperRef.current.slideToLoop(
                positionToRealIndex(position), 0);
        };

        window.addEventListener(
            'action-hex-event', actionHexListener);
        window.addEventListener(
            gamePlayerEventName, gamePlayerUpdateListener);
        return () => {
            window.removeEventListener(
                'action-hex-event', actionHexListener);
            window.removeEventListener(
                gamePlayerEventName, gamePlayerUpdateListener);
        };
    // app/seat are stable; the listener reads through propsRef.
    // eslint-disable-next-line
    }, [seat]);

    const getAndSendActions = (realIndex) => {
        const center1Id = slideAction(realIndex + 2);
        const center2Id = slideAction(realIndex + 3);
        currentActionsRef.current = [center1Id, center2Id];
        const position = realIndexToPosition(realIndex);
        currentPositionRef.current = position;
        window.dispatchEvent(new CustomEvent('action-hex-event', {
            detail: { seat: seat, actionHexPosition: position },
        }));
    };

    const handleSwiper = (swiper) => {
        // 2026-05-23: seed the initial slider position HERE, inside
        // onSwiper, rather than deferring to a firstLoad branch of
        // onSlideChange. With loop=true, Swiper fires an internal
        // layout-driven onSlideChange during loop-slide duplication
        // setup that can land BEFORE onSwiper resolves. The old
        // pattern flipped firstLoad to false unconditionally there,
        // locking the seed out forever. In dev React 19 StrictMode's
        // mount-unmount-mount cycle masked the race; in prod no
        // StrictMode, race lands, slider sits at realIndex 0 while
        // currentSwiperIndex says 10 → consistent -3/+9 (mod 12)
        // visual offset. Seeding from onSwiper guarantees `swiper`
        // is the just-resolved instance.
        swiperRef.current = swiper;
        if (!firstLoadRef.current) return;
        const raw = propsRef.current.gamePlayerData.action_hex_position;
        const position = (raw === null || raw === undefined)
            ? DEFAULT_ACTION_HEX_POSITION : raw;
        const initialSlide = positionToRealIndex(position);
        currentPositionRef.current = position;
        currentSwiperIndexRef.current = initialSlide;
        firstLoadRef.current = false;
        if (swiper && swiper.slideToLoop) {
            swiper.slideToLoop(initialSlide, 0);
        }
    };

    const onTouchStart = () => {
        if (isHexLocked()) return;
        userInputPendingRef.current = true;
    };

    const onTouchMove = () => {
        // Re-arm on every move during a drag. Without this,
        // userInputPending is consumed after the first tick and
        // every subsequent tick in the same drag is dropped by the
        // guard in onSlideChange. The slider snaps to the final
        // position visually but the hex/grid lock to wherever the
        // first tick reached. 2026-05-22.
        if (isHexLocked()) return;
        userInputPendingRef.current = true;
    };

    const onTouchEnd = (swiper) => {
        // Release-driven commit. Catches the case where the drag
        // crossed boundaries faster than onSlideChange fired for
        // each, and the case where the drag did not cross a
        // boundary at all but the user expected the position to
        // commit. userInputPending is left true so the snap-settle
        // onSlideChange that follows passes the reflow guard and
        // the final settled realIndex wins. 2026-05-22.
        if (isHexLocked()) return;
        if (firstLoadRef.current) return;
        userInputPendingRef.current = true;
        if (currentSwiperIndexRef.current !== swiper.realIndex) {
            currentSwiperIndexRef.current = swiper.realIndex;
            getAndSendActions(swiper.realIndex);
        }
    };

    const onSlideChange = (swiper) => {
        swiperRef.current = swiper;
        if (firstLoadRef.current) {
            // Loop-mode internal layout sweep fired before onSwiper.
            // Don't process — handleSwiper will seed when it lands.
            return;
        }
        if (isHexLocked()) {
            userInputPendingRef.current = false;
            return;
        }
        // Layout reflows fire onSlideChange without a preceding
        // user gesture. userInputPending is armed only while a
        // finger is interacting; drop reflow fires.
        if (!userInputPendingRef.current) return;
        if (currentSwiperIndexRef.current !== swiper.realIndex) {
            currentSwiperIndexRef.current = swiper.realIndex;
            getAndSendActions(swiper.realIndex);
        }
    };

    return (
        <div className={'action-scroller action-hex'}
             data-testid={'action-hex'}>
            <div className={'action-scroller-anchor'}>
                <div className={'swiper-container'}>
                    <Swiper
                        slidesPerView={6}
                        spaceBetween={0}
                        grabCursor={true}
                        loop={true}
                        watchSlidesProgress={true}
                        onSwiper={handleSwiper}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        onSlideChange={onSlideChange}
                        className={'action-scroller-swiper'}>
                        <SwiperSlide virtualIndex={1}>
                            <ActionImage texture={71}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={2}>
                            <ActionImage texture={71}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={3}>
                            <ActionImage texture={72}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={4}>
                            <ActionImage texture={72}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={5}>
                            <ActionImage texture={73}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={6}>
                            <ActionImage texture={73}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={7}>
                            <ActionImage texture={74}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={8}>
                            <ActionImage texture={74}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={9}>
                            <ActionImage texture={75}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={10}>
                            <ActionImage texture={75}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={11}>
                            <ActionImage texture={76}/>
                        </SwiperSlide>
                        <SwiperSlide virtualIndex={12}>
                            <ActionImage texture={76}/>
                        </SwiperSlide>
                    </Swiper>
                </div>
                <img
                    src={ASSETS_URL
                        + 'overlays/ActionSliderLayover-1080x210.png'}
                    alt={'action'}
                    className={'swiper-overlay'}/>
            </div>
        </div>
    );
}

// Exported for test parity with ActionChooser.test.js so the realIndex
// math can be asserted without remounting the component.
export const __test__ = {
    slideAction,
    realIndexToPosition,
    positionToRealIndex,
};

export default ActionHex;
